import React from "react"
import { Pressable, StyleSheet, Text, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import TextButtonM from "@common/components/button/TextButtonM"
import { theme } from "@common/theme/theme"
import EditLink from "../components/EditLink"

/**
 * Les blocs de l'écran de réservation du funnel d'inscription. Ils ne portent
 * que la forme : quels mots, quels montants, quel réglage s'ouvre — tout cela
 * reste à l'écran, qui le lit sur le devis serveur.
 *
 * Ils ne sont pas dans le kit commun des checkouts (`FunnelCheckoutCards`) :
 * l'été et le stage vendent un produit borné, sans échéancier ni réglage à
 * rouvrir, et gardent leur récap et leur bloc de prix.
 */

const DARK = theme.colors.primaryDark || theme.colors.primary

/**
 * La carte de la réservation : ce que le parent achète en tête, ses réglages,
 * puis ce qu'il paie et quand. Une seule surface, pour que l'œil entre par là.
 */
export const ReservationCard = ({ children }) => (
    <View style={styles.card}>{children}</View>
)

/**
 * La tête de la carte — ce que le parent achète d'un côté, ce que ça coûte de
 * l'autre. Le détail (matières, créneaux) s'ouvre à la demande : à cette étape
 * il vient de le composer.
 */
export const ReservationRecap = ({
    label,
    price,
    open,
    onToggle,
    children,
}) => (
    <View>
        <Pressable
            style={styles.recapHeader}
            onPress={onToggle}
            accessibilityRole="button"
            aria-expanded={open}
            accessibilityLabel={`${label}, voir le détail`}
        >
            <Text style={styles.recapLabel}>{label}</Text>
            {price ? <Text style={styles.recapPrice}>{price}</Text> : null}
            <FontAwesome5
                name={open ? "chevron-up" : "chevron-down"}
                size={10}
                color={DARK}
            />
        </Pressable>
        {open ? <View style={styles.recapBody}>{children}</View> : null}
    </View>
)

/**
 * Un réglage de la réservation, dans la carte : ce qu'il vaut, et « Modifier »
 * quand `onEdit` est donné. `children` reçoit le choix, ouvert sous la ligne.
 */
export const ReservationSetting = ({ label, value, onEdit, children }) => (
    <View>
        <View style={styles.setting}>
            <Text style={styles.settingLabel}>{label}</Text>
            <Text style={styles.settingValue}>{value}</Text>
            {onEdit ? <EditLink label={label} onPress={onEdit} /> : null}
        </View>
        {children}
    </View>
)

/**
 * La frise des paiements : `{ key, title, detail?, amount? }`, du premier au
 * dernier. Le premier point est plein et son montant vert — c'est le seul
 * réglé aujourd'hui. Un point sans montant ne fait rien régler.
 */
export const ReservationTimeline = ({ points }) => (
    <View style={styles.timeline}>
        {points.map((point, index) => {
            const today = index === 0
            const last = index === points.length - 1
            return (
                <View key={point.key} style={styles.point}>
                    <View style={styles.rail}>
                        <View style={[styles.dot, today && styles.dotToday]} />
                        {last ? null : <View style={styles.railLine} />}
                    </View>
                    <View style={[styles.pointText, !last && styles.pointGap]}>
                        <Text style={styles.pointTitle}>{point.title}</Text>
                        {point.detail ? (
                            <Text style={styles.pointDetail}>
                                {point.detail}
                            </Text>
                        ) : null}
                    </View>
                    {point.amount ? (
                        <Text
                            style={[
                                styles.pointAmount,
                                today && styles.pointAmountToday,
                            ]}
                        >
                            {point.amount}
                        </Text>
                    ) : null}
                </View>
            )
        })}
    </View>
)

/**
 * Les garanties qui lèvent le doute au moment de payer, chacune sous son
 * pictogramme, dans une seule bande : la toucher ouvre toutes les garanties.
 */
export const ReservationGuarantees = ({ items, onPress }) => (
    <Pressable
        style={styles.guarantees}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={`${items.map((item) => item.label).join(", ")}. Voir toutes les garanties`}
    >
        <View style={styles.icons}>
            {items.map((item) => (
                <View key={item.label} style={styles.iconItem}>
                    <FontAwesome5
                        name={item.icon}
                        size={16}
                        color={theme.colors.success}
                    />
                    <Text style={styles.guaranteeLabel}>{item.label}</Text>
                </View>
            ))}
        </View>
        <Text style={styles.guaranteesMore}>
            Toutes les garanties{"\u00a0"}
            <FontAwesome5
                name="chevron-right"
                size={9}
                color={theme.colors.successDark}
            />
        </Text>
    </Pressable>
)

/** Un lien centré sous un bloc, pour ouvrir ce qu'il résume. */
export const ReservationLink = ({ label, onPress }) => (
    <View style={styles.linkBox}>
        <TextButtonM
            label={label}
            onPress={onPress}
            fontWeight="Bold"
            numberOfLines={2}
            style={styles.link}
        />
    </View>
)

const styles = StyleSheet.create({
    card: {
        borderRadius: 14,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
    recapHeader: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingVertical: 14,
        paddingHorizontal: 16,
    },
    // Le format replie ses mots, jamais le prix.
    recapLabel: {
        flex: 1,
        fontFamily: "Montserrat-Bold",
        fontSize: 14,
        lineHeight: 19,
        color: DARK,
    },
    recapPrice: {
        flexShrink: 0,
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 15,
        color: DARK,
    },
    recapBody: {
        gap: 8,
        paddingHorizontal: 16,
        paddingBottom: 12,
    },
    setting: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 10,
        paddingVertical: 3,
    },
    settingLabel: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        lineHeight: 20,
        color: theme.colors.grey,
    },
    settingValue: {
        flex: 1,
        textAlign: "right",
        fontFamily: "Montserrat-SemiBold",
        fontSize: 13,
        lineHeight: 20,
        color: DARK,
    },
    timeline: {
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        marginTop: 10,
        paddingTop: 14,
    },
    point: {
        flexDirection: "row",
        alignItems: "stretch",
    },
    rail: {
        width: 22,
        alignItems: "center",
        paddingTop: 5,
    },
    dot: {
        width: 10,
        height: 10,
        borderRadius: 5,
        borderWidth: 2,
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.white,
    },
    dotToday: { backgroundColor: theme.colors.primary },
    railLine: {
        flex: 1,
        width: 2,
        marginTop: 3,
        marginBottom: -2,
        backgroundColor: theme.colors.lightMore,
    },
    pointText: {
        flex: 1,
        paddingLeft: 8,
        paddingRight: 10,
    },
    pointGap: { paddingBottom: 12 },
    pointTitle: {
        fontFamily: "Montserrat-Bold",
        fontSize: 14,
        lineHeight: 19,
        color: DARK,
    },
    pointDetail: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        lineHeight: 16,
        color: theme.colors.grey,
    },
    pointAmount: {
        flexShrink: 0,
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 15,
        lineHeight: 19,
        color: DARK,
    },
    pointAmountToday: { color: theme.colors.success },
    // Trois garanties de front : une légende trop longue passe à la ligne.
    icons: {
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "flex-start",
        columnGap: 4,
        rowGap: 10,
    },
    iconItem: {
        flex: 1,
        minWidth: 76,
        alignItems: "center",
        gap: 6,
    },
    guarantees: {
        marginTop: 14,
        paddingTop: 14,
        paddingBottom: 10,
        paddingHorizontal: 8,
        borderRadius: 14,
        backgroundColor: theme.colors.successLight,
        gap: 10,
    },
    guaranteeLabel: {
        fontFamily: "Montserrat-SemiBold",
        fontSize: 11.5,
        lineHeight: 15,
        color: DARK,
        textAlign: "center",
    },
    guaranteesMore: {
        fontFamily: "Montserrat-SemiBold",
        fontSize: 12,
        color: theme.colors.successDark,
        textAlign: "center",
    },
    // La boîte se serre sur le texte : seul le lien se touche, pas la ligne.
    linkBox: { alignSelf: "center" },
    link: { fontSize: 13, textAlign: "center", paddingVertical: 4 },
})
